import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "../ui/drawer";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Printer, Eye, Pencil, Archive, Trash2, RotateCcw, Snowflake, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formatDateIT = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split("T")[0].split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const LottoActionModal = ({
  open, onClose, lotto, prodottoNome,
  onPrint, onEdit, onArchive, onRestore, onDelete,
  onCongela, onScongela,
  isArchived, archiveEditView
}) => {
  const navigate = useNavigate();
  if (!lotto) return null;

  const handleVisualizza = () => {
    // Naviga nella pagina pubblica RESTANDO dentro l'app (stessa tab / PWA),
    // così da mobile si può chiudere e tornare indietro con il tasto "Chiudi".
    navigate(`/lotti/p/${lotto.id}`);
    onClose();
  };

  const dataScadenzaEffettiva = lotto.data_scadenza_aperto || lotto.data_scadenza;
  const hasScadenza = !!dataScadenzaEffettiva;
  const isComposto = lotto.is_composto;
  const dataCreazione = lotto.data_apertura || lotto.created_at?.split("T")[0];
  const stato = lotto.stato || "fresco";
  const isCongelato = stato === "congelato";
  const isScongelato = stato === "scongelato";
  // Nell'archivio le azioni e la posizione condividono un solo portale: niente
  // transizione fra Vaul e Radix mentre l'utente apre di nuovo lo stesso lotto.
  const inlineArchive = isArchived && archiveEditView !== undefined;
  const Title = inlineArchive ? DialogTitle : DrawerTitle;
  const Description = inlineArchive ? DialogDescription : DrawerDescription;

  // Days until expiry
  let daysText = "";
  let daysColor = "bg-emerald-600 text-white";
  if (hasScadenza) {
    const today = new Date(); today.setHours(0,0,0,0);
    const scadenza = new Date(dataScadenzaEffettiva); scadenza.setHours(0,0,0,0);
    const days = Math.ceil((scadenza - today) / (1000*60*60*24));
    if (days < 0) { daysText = "Scaduto"; daysColor = "bg-red-500 text-white"; }
    else if (days === 0) { daysText = "Oggi"; daysColor = "bg-red-500 text-white"; }
    else if (days === 1) { daysText = "Domani"; daysColor = "bg-amber-500 text-white"; }
    else if (days <= 7) { daysText = `${days} giorni`; daysColor = "bg-amber-500 text-white"; }
    else { daysText = `${days} giorni`; daysColor = "bg-emerald-600 text-white"; }
  }

  const actions = (
    <>
        <div className="text-left px-4 pt-4 pb-2">
          <Title className="text-base font-bold leading-tight pr-8" data-testid="lotto-action-name">{prodottoNome}</Title>
          <Description className="sr-only">Azioni disponibili per il lotto selezionato</Description>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {lotto.numero_lotto && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">{lotto.numero_lotto}</span>
            )}
            {isComposto && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 font-bold">Preparato</span>
            )}
            {isCongelato && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-600 font-bold inline-flex items-center gap-1" data-testid="badge-congelato">
                <Snowflake className="h-2.5 w-2.5" /> CONGELATO
              </span>
            )}
            {isScongelato && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-600 font-bold inline-flex items-center gap-1" data-testid="badge-scongelato">
                <Sun className="h-2.5 w-2.5" /> SCONGELATO
              </span>
            )}
            {daysText && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${daysColor}`}>{daysText}</span>
            )}
          </div>
        </div>

        <div className="px-4 pb-2">
          {/* Info compatta su una riga */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground py-2 border-y border-border">
            <div><span className="uppercase text-[9px] tracking-wider block">Creato</span><span className="text-foreground font-medium text-sm">{formatDateIT(dataCreazione)}</span></div>
            <div className="w-px h-8 bg-border" />
            <div><span className="uppercase text-[9px] tracking-wider block">Scadenza</span><span className="text-foreground font-medium text-sm">{formatDateIT(dataScadenzaEffettiva)}</span></div>
            {lotto.quantita_testo && (<><div className="w-px h-8 bg-border" /><div><span className="uppercase text-[9px] tracking-wider block">Qtà</span><span className="text-foreground font-medium text-sm">{lotto.quantita_testo}</span></div></>)}
            {lotto.operatore && (<><div className="w-px h-8 bg-border" /><div><span className="uppercase text-[9px] tracking-wider block">Operatore</span><span className="text-foreground font-medium text-sm">{lotto.operatore}</span></div></>)}
          </div>
        </div>

        {/* Azioni */}
        <div className="px-4 pb-6 space-y-1.5">
          <button onClick={() => { onPrint(lotto); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm active:scale-[0.98] transition-all"
            data-testid="action-print">
            <Printer className="h-4 w-4" /> Stampa etichetta
          </button>

          <button onClick={handleVisualizza}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border text-foreground text-sm active:scale-[0.98] transition-all"
            data-testid="action-visualizza">
            <Eye className="h-4 w-4" /> Visualizza
          </button>

          <button onClick={() => { onEdit(lotto); if (!inlineArchive) onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border text-foreground text-sm active:scale-[0.98] transition-all"
            data-testid="action-edit">
            <Pencil className="h-4 w-4" /> {isArchived ? "Modifica posizione" : "Modifica"}
          </button>

          {!isArchived && !isCongelato && onCongela && (
            <button onClick={() => { onCongela(lotto); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 dark:bg-sky-500/10 dark:border-sky-500/30 dark:text-sky-400 font-bold text-sm active:scale-[0.98] transition-all"
              data-testid="action-congela">
              <Snowflake className="h-4 w-4" /> Congela
            </button>
          )}

          {!isArchived && isCongelato && onScongela && (
            <button onClick={() => { onScongela(lotto); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-400 font-bold text-sm active:scale-[0.98] transition-all"
              data-testid="action-scongela">
              <Sun className="h-4 w-4" /> Scongela
            </button>
          )}

          {isArchived ? (
            <button onClick={() => { onRestore(lotto.id); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border text-foreground text-sm active:scale-[0.98] transition-all"
              data-testid="action-restore">
              <RotateCcw className="h-4 w-4" /> Ripristina
            </button>
          ) : (
            <button onClick={() => { onArchive(lotto); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border text-foreground text-sm active:scale-[0.98] transition-all"
              data-testid="action-archive">
              <Archive className="h-4 w-4" /> Archivia
            </button>
          )}

          <button onClick={() => { onDelete(lotto.id); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 text-sm active:scale-[0.98] transition-all"
            data-testid="action-delete">
            <Trash2 className="h-4 w-4" /> Elimina
          </button>
        </div>
    </>
  );

  if (inlineArchive) return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-y-auto" data-testid={archiveEditView ? "lotto-action-editor-shell" : "lotto-action-drawer"}>
        {archiveEditView || actions}
      </DialogContent>
    </Dialog>
  );

  return (
    <Drawer open={open} shouldScaleBackground={false} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DrawerContent className="max-h-[80dvh] overflow-y-auto" data-testid="lotto-action-drawer">
        {actions}
      </DrawerContent>
    </Drawer>
  );
};

export default LottoActionModal;
